import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, CircularProgress, Divider, TableCell, TableRow, TableHead, Table, TableBody, TableContainer } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { GET_INVOICE_EXPORT_PROFORMA_INVOICE, READ_PACKING_LIST_EXPORT } from '../../../services/apiRoutes';
import CustomCardView from "../../common/CustomCardView";
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import CustomPrintModel from "../../common/CustomPrint";
import { printFunction } from "../../../services/apiServices";
import { genericPost } from "../../../services/authServices";
import { PackingListPrintFirst, PackingListPrintFour, PackingListPrintSecond } from "../PrintPdf/PackingListPrint";
import { useCompanyDateFormat } from "../../../constants/useCompanyDateFormat";
import { t } from "../../../constants/translationHelper";


const PackingListView = ({ handleClose, setMessage, fetchData, packingListId, companyName, phoneNumber, shipmentOrgId, templateId }) => {
    const companyDateFormat = useCompanyDateFormat();

    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [termConditions, setTermConditions] = useState([]);
    const [companyBanks, setCompanyBanks] = useState([]);
    const [shipments, setShipments] = useState([]);
    const [shippingBooking, setShippingBooking] = useState([]);
    const [paymentVoucher, setPaymentVoucher] = useState([]);
    const [companyTaxes, setCompanyTaxes] = useState([]);
    const [shippingmark, setShippingmark] = useState([]);
    const [coaRecords, setCoaRecords] = useState([]);
    const [salesContracts, setSalesContracts] = useState([]);

    useEffect(() => {
        fetchDataInvoice();
    }, []);

    const fetchDataInvoice = async () => {
        setLoading(true);
        try {
            const payload = { shipmentOrgId: shipmentOrgId };
            const response = await genericPost(GET_INVOICE_EXPORT_PROFORMA_INVOICE, payload);
            if (response?.data?.success) {
                const resData = response.data.data;
                setTermConditions(resData.termConditions || []);
                setCompanyBanks(resData.companyBanks || []);
                setShipments(resData.shipments || []);
                setShippingBooking(resData.shippingBooking || []);
                setPaymentVoucher(resData.paymentVoucher || []);
                setCompanyTaxes(resData.companyTaxes || []);
                setShippingmark(resData.shippingmark || []);
                setCoaRecords(resData.coaRecords || []);
                setSalesContracts(resData.salesContracts || []);
            } else {
                setError('No data found');
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Error fetching data');
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (!packingListId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                const response = await axios.get(READ_PACKING_LIST_EXPORT(packingListId), {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                setData(response.data.data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [packingListId]);

    if (loading) return <CircularProgress />;
    if (error) return <p>Error: {error}</p>;

    const formatPort = (port, country) => [port, country].filter(Boolean).join(", ");


    const values = {
        packingListId: data?.packingListId || '',
        userId: data?.userId || '',
        companyId: data?.companyId || '',
        clientCompanyId: data?.clientCompanyId || '',
        addressTo: data?.addressTo || '',
        invNo: data?.commercialInv?.commercialInvoiceNo || '',
        date: companyDateFormat(data?.date) || '',
        loadingPort: formatPort(
            data?.loading?.portName,
            data?.loading?.country?.name
        ),
        destinationPort: formatPort(
            data?.destination?.portName,
            data?.destination?.country?.name
        ),
        mark: data?.mark || '',
        description: data?.description || '',
        quantity: data?.quantity || '',
        pkg: data?.pkg || '',
        nw: data?.nw || '',
        tw: data?.tw || '',
        measurement: data?.measurement || '',
        totalPackedIn: data?.totalPackedIn || '',
        totalGrossWeight: data?.totalGrossWeight || '',
        totalNetWeight: data?.totalNetWeight || '',
        casNo: data?.casNo || '',
        iupac: data?.iupac || '',
        createdAt: data?.createdAt || '',
        updatedAt: data?.updatedAt || '',
    };
    const labels = [
        { key: 'addressTo', label: 'To', sm: 12, md: 6 },
        { key: 'invNo', label: 'Inv. No', sm: 12, md: 3, },
        { key: 'date', label: 'date', sm: 12, md: 3, },
        { key: 'loadingPort', label: 'Loading Port', sm: 12, md: 6, sx: { pt: 2 } },
        { key: 'destinationPort', label: 'Destination Port', sm: 12, md: 6, sx: { pt: 2 } },
    ];
    const labelFooter = [
        { key: 'totalPackedIn', label: 'Total Packed In', sm: 12, md: 12, },
        { key: 'totalGrossWeight', label: 'Total Gross Weight', sm: 12, md: 12, sx: { pt: 2 } },
        { key: 'totalNetWeight', label: 'Total Net Weight', sm: 12, md: 12, sx: { pt: 2 } },
        { key: 'casNo', label: 'CAS No', sm: 12, md: 6, sx: { pt: 2 } },
        { key: 'iupac', label: 'IUPAC', sm: 12, md: 6, sx: { pt: 2 } },
    ];

    const clientCompanyName = data?.customer?.clientCompanyName || '';
    const encodedCompanyName = clientCompanyName.replace(/ /g, '').toLowerCase();
    const fileName = `commercial_invoice_${encodedCompanyName}.pdf`;

    const printContent = () => {
        const elementId = 'contentId';
        const iframeId = 'print-iframe';
        printFunction(elementId, iframeId, fileName);
    };
    const pdfContent = () => {
        const elementId = 'contentpdf';
        const iframeId = 'print-iframe';
        printFunction(elementId, iframeId, fileName);
    };

    // const check = parseInt(templateId);
    const footerFalse = false;
    const footerTrue = true;

    const check = parseInt(templateId);

    const getPrintContent = () => {
        if (check === 1) {
            return (<PackingListPrintFirst
                invNo={values.invNo}

                invoiceData={data}
                termConditions={termConditions}
                companyBanks={companyBanks}
                shippingBooking={shippingBooking}
                shipments={shipments}
                paymentVoucher={paymentVoucher}
                companyTaxes={companyTaxes}
                shippingmark={shippingmark}
                coaRecords={coaRecords}
                salesContracts={salesContracts}
                footer={footerFalse} />);
        }
        if (check === 2) {
            return (
                <PackingListPrintSecond
                    invNo={values.invNo}

                    invoiceData={data}
                    termConditions={termConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    footer={footerFalse} />);
        }
        if (check === 3) {
            return (
                <PackingListPrintFour
                    invNo={values.invNo}

                    invoiceData={data}
                    termConditions={termConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    footer={footerTrue} />);
        }
        if (check === 4) {
            return (
                <PackingListPrintFour
                    invNo={values.invNo}

                    invoiceData={data}
                    termConditions={termConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    footer={footerFalse} />);
        }

        return <PackingListPrintFirst
            invNo={values.invNo}

            invoiceData={data}
            termConditions={termConditions}
            companyBanks={companyBanks}
            shippingBooking={shippingBooking}
            shipments={shipments}
            paymentVoucher={paymentVoucher}
            companyTaxes={companyTaxes}
            shippingmark={shippingmark}
            coaRecords={coaRecords}
            salesContracts={salesContracts}
            footer={footerFalse} />;
    };

    const getPdfContent = () => {
        if (check === 1) {
            return (<PackingListPrintFirst
                invNo={values.invNo}

                invoiceData={data}
                termConditions={termConditions}
                companyBanks={companyBanks}
                shippingBooking={shippingBooking}
                shipments={shipments}
                paymentVoucher={paymentVoucher}
                companyTaxes={companyTaxes}
                shippingmark={shippingmark}
                coaRecords={coaRecords}
                salesContracts={salesContracts}
                footer={footerTrue} />);
        }
        if (check === 2) {
            return (
                <PackingListPrintSecond
                    invNo={values.invNo}

                    invoiceData={data}
                    termConditions={termConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    footer={footerTrue} />);
        }
        if (check === 3) {
            return (
                <PackingListPrintFour
                    invNo={values.invNo}

                    invoiceData={data}
                    termConditions={termConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    footer={footerTrue} />);
        }
        if (check === 4) {
            return (
                <PackingListPrintFour
                    invNo={values.invNo}

                    invoiceData={data}
                    termConditions={termConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    footer={footerTrue} />);
        }

        return <PackingListPrintFirst
            invNo={values.invNo}

            invoiceData={data}
            termConditions={termConditions}
            companyBanks={companyBanks}
            shippingBooking={shippingBooking}
            shipments={shipments}
            paymentVoucher={paymentVoucher}
            companyTaxes={companyTaxes}
            shippingmark={shippingmark}
            coaRecords={coaRecords}
            salesContracts={salesContracts}
            footer={footerTrue} />;
    };

    return (
        <>
            <CustomPrintModel contentId="contentId">
                {getPrintContent()}
            </CustomPrintModel >

            <CustomPrintModel contentId="contentpdf">
                {getPdfContent()}
            </CustomPrintModel >
            <Box sx={{ mx: 'auto', mt: 0 }}>
                <Formik initialValues={values} enableReinitialize>
                    {() => (
                        <Form>
                            <Grid container spacing={1}>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontWeight: 600, fontSize: "18px", color: "#212529" }}>
                                        {t("Packing List")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8} style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Box>
                                        <IconButton aria-label="delete">
                                            <DeleteOutlineOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="print" onClick={printContent}>
                                            <LocalPrintshopOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="print" onClick={pdfContent}>
                                            <PictureAsPdfOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="close" onClick={handleClose} >
                                            <CloseIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Divider />
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={12} md={12} lg={12}>
                                    <Box style={{ margin: "auto", padding: "auto", textAlign: "center" }}>
                                        <Typography style={{ color: "#212529", fontWeight: 500, fontSize: "15px", textTransform: "uppercase" }}>
                                            {t("Packing List")}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={12} md={12} lg={12}>
                                    <CustomCardView label={labels} data={values} />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12} lg={12}>
                                    <TableContainer style={{ paddingTop: "0px" }}>
                                        <Table
                                            style={{
                                                width: "100%",
                                                border: "1px solid #dee2e6",
                                                borderCollapse: "collapse",
                                            }}
                                        >

                                            <TableHead style={{ height: '40px', backgroundColor: "#e7f0f7" }}>
                                                <TableRow style={{ border: "1px solid #dee2e6" }}>
                                                    <TableCell style={cellLabel}>
                                                        {t("Mark")}
                                                    </TableCell>
                                                    <TableCell style={cellLabel}>
                                                        {t("Description")}
                                                    </TableCell>
                                                    <TableCell style={cellLabel}>
                                                        {t("Quantity")}
                                                    </TableCell>
                                                    <TableCell style={cellLabel}>{t("PKG")}</TableCell>
                                                    <TableCell style={cellLabel}>
                                                        {t("Net Weight")}
                                                    </TableCell>
                                                    <TableCell style={cellLabel}>
                                                        {t("Gross Weight")}
                                                    </TableCell>
                                                    <TableCell style={cellLabel}>
                                                        {t("Measurement")}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow
                                                    style={{
                                                        border: "1px solid #dee2e6",
                                                        color: "#212529",
                                                        fontWeight: "600",
                                                        fontSize: "14px",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "clip",
                                                    }}
                                                >
                                                    <TableCell style={tableCell}>{values.mark}</TableCell>
                                                    <TableCell style={tableCell}>{values.description}</TableCell>
                                                    <TableCell style={tableCell}>{values.quantity}</TableCell>
                                                    <TableCell style={tableCell}>{values.pkg}</TableCell>
                                                    <TableCell style={tableCell}>{values.nw}</TableCell>
                                                    <TableCell style={tableCell}>{values.tw}</TableCell>
                                                    <TableCell style={tableCell}>{values.measurement}</TableCell>
                                                </TableRow>
                                            </TableBody>
                                            <TableRow style={{ height: '40px', backgroundColor: "#e7f0f7" }}>
                                                <TableCell style={tablecellfooter} colSpan={2}  > {t("Total")}</TableCell>
                                                <TableCell style={tablecellfooter}  > {values.quantity}</TableCell>
                                                <TableCell style={tablecellfooter}  >{values.pkg} </TableCell>
                                                <TableCell style={tablecellfooter} > {values.nw}</TableCell>
                                                <TableCell style={tablecellfooter} > {values.tw}</TableCell>
                                                <TableCell style={tablecellfooter} >{values.measurement} </TableCell>
                                            </TableRow>
                                        </Table>
                                    </TableContainer>

                                    <Grid item xs={12} sm={12} md={12} lg={12}>
                                        <br />
                                        <CustomCardView label={labelFooter} data={values} />
                                    </Grid>

                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </Box >

        </>

    );
};

export default PackingListView;


const cellLabel = {
    border: "1px solid #dee2e6",
    color: "#212529",
    fontWeight: "600",
    fontSize: "14px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "clip",
    padding: "5px",
    textAlign: 'center',

}

const tableCell = {
    border: "1px solid #dee2e6",
    padding: "5px",
    textAlign: "left"

}

const tablecellfooter = {
    border: '1px solid #dee2e6',
    color: '#212529',
    fontWeight: 600,
    fontSize: '14px',
    textAlign: 'right',
    padding: '1px',
    paddingLeft: "10px",
    paddingRight: "10px",

}
